import DayGlucoseGraphHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/day-glucose-graph.webp";
import DailyGlucoseFluctuationHowToImprove from "src/assets/images/cgm/detailed-logs/how-to-read/daily-glucose-fluctuation.webp";
import HighOrLowGlucoseAreasHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/high-or-low-glucose-areas.webp";
import GlucoseConcentrationProfileHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/glucose-concentration-profile.webp";
import TimeBelowRangeHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/time-below-range.webp";
import AverageGlucoseHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/average-glucose.webp";
import MinGlucoseHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/min-glucose.webp";
import TimeAboveRangeHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/time-above-range.webp";
import TimeInRangeHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/time-in-range.webp";
import MaxGlucoseHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/max-glucose.webp";
import AGPOverlapGraphHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/agp-overlap-map.webp";
import EstimatedHbA1cHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/estimated-HbA1c.webp";
import GlucoseVariabilityHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/glucose-variability.webp";
import StandardDeviationHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/standard-deviation.webp";
import PercentageTimeInRangesHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/percentage-time-in-ranges.webp";
import FluctuationSummaryHowToRead from "src/assets/images/cgm/detailed-logs/how-to-read/max-fluctuation-summary.webp";

import { cgmDetailedLogsScreens } from "./cgm";

export const cgmDetailedLogs = {
  [cgmDetailedLogsScreens.DAY_GLUCOSE_GRAPH]: {
    title: "Day Glucose Graph",
    description: "View your daily glucose levels",
    whatItMeansSection: {
      title: "What It Means?",
      description: `This Day Glucose Graph is like your personal health timeline.
      It shows how your sugar levels changed through the day—when you stayed steady,
      when things dipped, and when you had a bit of a spike. <br /><br />
      The green zone is your sweet spot. Anytime your line stays in there, your body's loving it.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: DayGlucoseGraphHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "Here's how you can smoothen that rollercoaster:",
          list: [
            "Time your meals and meds: Try not to delay meals or insulin.",
            "Always keep a snack handy for sudden lows.",
            "Add light activity after eating—like a short walk.",
            "Night-time dips? Check if your dinner or insulin needs a tweak.",
            "Track patterns over a few days—it helps you and your coach/doctor spot what's working and what's not.",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.DAILY_GLUCOSE_FLUCTUATION]: {
    title: "Daily Glucose Fluctuation",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Think of Max Glucose Fluctuation like your glucose rollercoaster's tallest hill and deepest dip on a given day.
        It captures how far your sugar levels swung—from your lowest low to your highest high.
        <br /><br />
        Even if your average sugar looks fine, big swings can still be risky.They may cause discomfort (fatigue, mood swings, cravings)
        or even long-term issues. This number tells you how stable—or unpredictable—your glucose day really was`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: DailyGlucoseFluctuationHowToImprove,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "For Big Post-Meal Spikes:",
          list: [
            "Take insulin 15–20 mins before eating",
            "Pick lower glycemic index foods",
            "Add protein/fats to slow down sugar absorption",
            "Eat smaller, frequent meals",
            "Recheck insulin-to-carb ratio with your doctor",
          ],
        },
        {
          sectionTitle: "For Sudden Glucose Drops:",
          list: [
            "Watch insulin timing and dose",
            'Don\'t "stack" insulin (multiple doses too close together)',
            "For heavy/fatty meals, consider dual wave boluses",
            "Adjust insulin if you're doing a workout",
          ],
        },
        {
          sectionTitle: "For Sudden Glucose Drops:",
          list: [
            "Keep your meal timing regular",
            "Try CGM alerts for early warnings",
            "Explore automated insulin systems if available",
            "Stay active daily and manage stress",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.HIGH_OR_LOW_GLUCOSE_AREAS]: {
    title: "High/low glucose areas",
    whatItMeansSection: {
      title: "What It Means?",
      description: `The High/Low Glucose Areas chart shows you the longest periods your sugar levels stayed outside
        the safe zone—either too high or too low—throughout your day.
        <br /><br />
        Why it matters? These long stretches tell us when your body is under stress.
        Maybe it's a post-meal spike, an overnight dip, or a missed snack.
        Spotting these zones helps you—and your coach—pinpoint and fix what's not working in your day.`,
    },

    howToReadSection: {
      title: "How to Read It?",
      image: HighOrLowGlucoseAreasHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "High Glucose Areas:",
          list: [
            "Take insulin a bit early: 15–20 mins pre-meal",
            "Check your carb counting—small errors add up",
            "Choose low glycemic index foods",
            "Morning highs? Ask your doc about adjusting basal insulin",
            "Walk after meals to flatten those glucose hills",
          ],
        },
        {
          sectionTitle: "Low Glucose Areas:",
          list: [
            "Review basal insulin dose if you dip often",
            "Add snacks during usual low windows",
            "Going low at night? Try a bedtime protein + carb snack",
            "Workout-related lows? Adjust insulin before activity or snack up!",
          ],
        },
        {
          sectionTitle: "General Game Plan:",
          list: [
            "Keep a simple log of food, insulin, and activities",
            "Stay consistent with meal timing",
            "Work with your doctor to fine-tune insulin-to-carb ratios",
            "If possible, try automated insulin delivery tech",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.GLUCOSE_CONCENTRATION_PROFILE]: {
    title: "Glucose Concentration Profile",
    whatItMeansSection: {
      title: "What It Means?",
      description: `The Glucose Concentration Profile is like a bird's-eye view of where your sugar readings usually hang out.
      Instead of looking at time, this chart shows how often your levels fall into each glucose range—low, target, or high.
      <br /><br />
      It helps you answer: "Am I mostly in control or swinging too far too often?"`,
    },

    howToReadSection: {
      title: "How to Read It?",
      image: GlucoseConcentrationProfileHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "If You Have Too Many Lows (<70 mg/dL):",
          list: [
            "Check insulin dose/timing",
            "Add between-meal snacks if needed",
            "Adjust insulin timing for workouts",
          ],
        },
        {
          sectionTitle: "If You Have Too Many Highs (>180 mg/dL):",
          list: [
            "Watch for hidden carbs or overeating",
            "Choose lower GI foods",
            "Talk to your doctor about increasing insulin ratios",
          ],
        },
        {
          sectionTitle: "If You Swing Between Highs and Lows:",
          list: [
            "Stick to consistent meals and timing",
            "Use automated insulin tech if available",
            "Balance both basal and bolus insulin strategies",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.TIME_BELOW_RANGE]: {
    title: "Time Below Range",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Time Below Range (TBR) shows how much time your glucose levels drop below 70 mg/dL—that's when your body may not have enough sugar to function at its best`,
    },

    howToReadSection: {
      title: "How to Read It?",
      image: TimeBelowRangeHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "For Big Post-Meal Spikes:",
          list: [
            "Don't skip meals, and try to eat a mix of carbs, protein, and fiber",
            "Keep a quick snack (like glucose tablets, fruit juice, or candy) handy—just in case",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.TIME_ABOVE_RANGE]: {
    title: "Time Above Range",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Time Above Range (TAR) tracks how often your glucose levels go above 200 mg/dL—this means your body may be in a state of hyperglycemia.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: TimeAboveRangeHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",

          list: [
            "Pick lower glycemic index foods and balance carbs with protein/fiber",
            "Take a short post-meal walk to reduce spikes",
            "Talk to your doctor about pre-meal insulin timing or correction doses",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.TIME_IN_RANGE]: {
    title: "Time in Range",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Time In Range (TIR) tells you how often your glucose levels stay between 70–200 mg/dL—the sweet spot where your body works best.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: TimeInRangeHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Eat meals at consistent times with a good mix of carbs, protein & fiber",
            "Monitor trends and adjust food or medication with your doctor’s help",
            "Light activity after meals can help smooth out spikes",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.AVG_GLUCOSE]: {
    title: "Average Glucose",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Average glucose is the mean of all your readings over the monitoring period. It gives you a big-picture view of your glucose control.`,
    },

    howToReadSection: {
      title: "How to Read It?",
      image: AverageGlucoseHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Eat smart: Go for low-GI, fiber-rich foods and reduce sugary carbs",
            "Move regularly: Aim for 150+ mins of moderate weekly activity",
            "Stick to meds: Take them on time and talk to your doc if changes are needed",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.MIN_GLUCOSE]: {
    title: "Min Glucose",

    whatItMeansSection: {
      title: "What It Means?",

      description: `Min Glucose is the lowest recorded glucose value during your monitoring period. It highlights how low your sugar levels dropped—which can be risky if too low.`,
    },

    howToReadSection: {
      title: "How to Read It?",

      image: MinGlucoseHowToRead,
    },

    howToImproveSection: {
      title: "How to Improve?",

      sections: [
        {
          sectionTitle: "",

          list: [
            "Keep a quick sugar source handy (like glucose tablets or fruit juice)",

            "Don’t skip meals and spread carbs evenly across the day",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.MAX_GLUCOSE]: {
    title: "Max Glucose",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Max Glucose shows the highest glucose value captured. It tells you how high your spikes went—often after meals or stress.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: MaxGlucoseHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Pair carbs with protein and fiber for slower absorption",
            "Time insulin (or meals) to avoid sharp spike",
            "Light activity after eating helps bring numbers down",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.AGP_OVERLAP_GRAPH]: {
    title: "AGP Overlap Graph",
    whatItMeansSection: {
      title: "What It Means?",
      description: `The AGP Graph is like your glucose diary, all rolled into one chart! It shows your typical daily sugar patterns using data from multiple days—highlighting when your levels are steady, spiking, or dipping.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: AGPOverlapGraphHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Time meals and insulin smartly—avoid delays",
            "Always keep a quick snack handy for low dips",
            "Notice night-time lows? Talk to your doctor about adjusting dinner or meds",
            "Try light walks post meals to smoothen highs",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.HBA1C]: {
    title: "Estimated HbA1c",
    whatItMeansSection: {
      title: "What It Means?",
      description: `<p>Estimated HbA1c gives you a snapshot of your average blood sugar over the past 2–3 months—without needing a lab test!</p><p>It’s calculated from your CGM data and helps you see how close you are to your long-term glucose goals.</p>`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: EstimatedHbA1cHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Don’t skip check-ups—use your data to guide your next steps!",
            "Manage stress, sleep well, and drink enough water",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.GLUCOSE_VARIABILITY]: {
    title: "Glucose Variability",
    whatItMeansSection: {
      title: "What It Means?",
      description: `<p>Glucose Variability shows how much your sugar levels swing up and down during the day. It's measured as CV% (Coefficient of Variation)—lower is better!</p><p>📐 Formula: CV% = (Standard Deviation ÷ Mean Glucose) × 100</p>`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: GlucoseVariabilityHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Eat similar carbs each day, and spread meals out evenly",
            "Take meds on time and consider pre-bolusing if needed",
            "Exercise consistently, and adjust food/insulin around it",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.STANDARD_DEVIATION]: {
    title: "Standard Deviation",
    whatItMeansSection: {
      title: "What It Means?",
      description: `Standard Deviation tells you how much your glucose levels vary from your average. A lower SD means your numbers stay steady—less of a rollercoaster ,more of a calm cruise `,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: StandardDeviationHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "",
          list: [
            "Stick to balanced meals with steady carb intake",
            "Time your meds and meals consistently",
            "Add post-meal activity like walking to smoothen highs",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.PERCENTAGE_TIME_IN_RANGES]: {
    title: "Percentage Time in Ranges",
    whatItMeansSection: {
      title: "What It Means?",
      description: `This colorful ring shows the cumulative time your glucose levels spent in different zones. Think of it as your personal glucose scoreboard`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: PercentageTimeInRangesHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "Here’s how you can smoothen that rollercoaster:",
          list: [
            "Review this chart weekly to track your progress over time!",
            "Light activity after meals can reduce spikes",
            "Don’t delay meals or meds",
            "Eat balanced meals—carbs + protein + fiber",
          ],
        },
      ],
    },
  },
  [cgmDetailedLogsScreens.FLUCTUATION_SUMMARY]: {
    title: "Fluctuation Summary",
    whatItMeansSection: {
      title: "What It Means?",
      description: `This graph is your blood sugar rollercoaster meter. Large daily fluctuations (a.k.a. high glycemic variability) can feel like sugar highs and crashes — they’re tough on your body and mood, even if your average sugar looks okay.`,
    },
    howToReadSection: {
      title: "How to Read It?",
      image: FluctuationSummaryHowToRead,
    },
    howToImproveSection: {
      title: "How to Improve?",
      sections: [
        {
          sectionTitle: "Here’s how you can smoothen that rollercoaster:",
          list: [
            "Balance your meals – avoid big carb-only meals",
            "Watch sugary drinks/snacks – they cause spikes and dips",
            "Move after eating – a 10-min walk helps sugar stabilize",
            "Track trends, not just one day – use this graph weekly!",
          ],
        },
      ],
    },
  },
};
